import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";
import { AssemblyProvider } from "./context/AssemblyContext";
import { AppWrapper } from "./components/AppWrapper";
import { ConfirmDialogProvider } from "./components/confirm/ConfirmDialogProvider";
import NotificationSystem from "./components/notifications/NotificationSystem";
import ProtectedRoute from "./components/ProtectedRoute";
import LoadingState from "./components/common/LoadingState";
import Login from "./components/auth/Login";
// import Register from "./components/auth/Register";

// Import pages
import LandingPage from "./pages/LandingPage";
import ManualPage from "./pages/ManualPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import OwnerDashboardPage from "./pages/OwnerDashboardPage";
import AdminAssembliesPage from "./pages/admin/AdminAssembliesPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminImportPage from "./pages/admin/AdminImportPage";
import AdminComplexesPage from "./pages/admin/AdminComplexesPage";
import AdminQueuePage from "./pages/admin/AdminQueuePage";
import AdminDelegationsPage from "./pages/admin/AdminDelegationsPage";
import AdminPowersPage from "./pages/admin/AdminPowersPage";
import AdminOwnerOutreachPage from "./pages/admin/AdminOwnerOutreachPage";
import ManagerDelegationsPage from "./pages/manager/ManagerDelegationsPage";
import ManagerOwnerOutreachPage from "./pages/manager/ManagerOwnerOutreachPage";
import BuilderPowersPage from "./pages/builder/BuilderPowersPage";
import ProfilePage from "./pages/shared/ProfilePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Import delegation pages
import DelegatePage from "./pages/DelegatePage";
import AcceptDelegationPage from "./pages/AcceptDelegationPage";
import RejectDelegationPage from "./pages/RejectDelegationPage";


// LoginPage wrapper
const LoginPage: React.FC = () => {
  const { login, isLoading, error, clearError } = useAuth();

  const handleLogin = async (email: string, password: string) => {
    if (error) clearError();
    await login({ email, password });
  };

  return <Login onLogin={handleLogin} loading={isLoading} error={error || undefined} />;
};

// RegisterPage wrapper
/*
const RegisterPage: React.FC = () => {
  const { register, isLoading, error, clearError } = useAuth();

  const handleRegister = async (data: any) => {
    if (error) clearError();
    try {
      await register(data);
    } catch (registrationError) {
      console.error("Registration failed:", registrationError);
      throw registrationError;
    }
  };

  return <Register onRegister={handleRegister} loading={isLoading} error={error || undefined} />;
};
*/

// RoleBasedRedirect - redirige según el rol del usuario
const RoleBasedRedirect: React.FC = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingState layout="page" fullScreen message="Cargando..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'admin') {
    return <Navigate to="/admin/assemblies" replace />;
  }

  if (user.role === 'manager') {
    return <Navigate to="/manager/delegations" replace />;
  }

  if (user.role === 'builder') {
    return <Navigate to="/builder/powers" replace />;
  }

  // Propietarios van al dashboard de participación
  return <Navigate to="/dashboard" replace />;
};

// Componente principal de rutas
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  // Marcar que la carga inicial ha terminado
  React.useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Solo mostrar loading screen durante la carga INICIAL
  if (isLoading && isInitialLoad) {
    return <LoadingState layout="page" fullScreen message="Cargando..." />;
  }

  return (
    <Routes>
      {/* Rutas públicas */}
      <Route
        path="/"
        element={
          isAuthenticated ? <RoleBasedRedirect /> : <LandingPage />
        }
      />
      <Route path="/manual" element={<ManualPage />} />
      <Route path="/legal/terms" element={<TermsPage />} />
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      <Route
        path="/login"
        element={
          isAuthenticated ? <RoleBasedRedirect /> : <LoginPage />
        }
      />
      {/* 
        ⚠️ RUTA DE REGISTRO DESHABILITADA
        Los usuarios se crean únicamente a través de la importación masiva por Excel (solo admin)
        Los usuarios no pueden auto-registrarse en el sistema
        
        TODO: Cuando sea necesario, descomentar esta ruta
      */}
      {/* <Route
        path="/register"
        element={
          isAuthenticated ? <RoleBasedRedirect /> : <RegisterPage />
        }
      /> */}
      <Route
        path="/forgot-password"
        element={
          isAuthenticated ? <RoleBasedRedirect /> : <ForgotPasswordPage />
        }
      />
      <Route
        path="/reset-password"
        element={
          isAuthenticated ? <RoleBasedRedirect /> : <ResetPasswordPage />
        }
      />

      {/* Rutas públicas de delegación */}
      <Route path="/delegar/:assemblyId" element={<DelegatePage />} />
      <Route path="/delegacion/aceptar/:id" element={<AcceptDelegationPage />} />
      <Route path="/delegacion/rechazar/:id" element={<RejectDelegationPage />} />

      {/* Ruta para PROPIETARIOS (owners) - Solo participación */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={["owner", "tenant", "guest", "admin", "manager"]}>
            <OwnerDashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Rutas para ADMIN - Gestión con sidebar */}
      <Route
        path="/admin/assemblies"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminAssembliesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/complexes"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminComplexesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/queue"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminQueuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/delegations"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDelegationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/powers"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminPowersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/outreach"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminOwnerOutreachPage />
          </ProtectedRoute>
        }
      />
      
      {/* Rutas para MANAGER - Solo delegaciones del conjunto asignado */}
      <Route
        path="/manager/delegations"
        element={
          <ProtectedRoute allowedRoles={["manager"]}>
            <ManagerDelegationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/outreach"
        element={
          <ProtectedRoute allowedRoles={["manager"]}>
            <ManagerOwnerOutreachPage />
          </ProtectedRoute>
        }
      />
      <Route path="/manager" element={<Navigate to="/manager/delegations" replace />} />

      <Route
        path="/builder/powers"
        element={
          <ProtectedRoute allowedRoles={["builder"]}>
            <BuilderPowersPage />
          </ProtectedRoute>
        }
      />
      <Route path="/builder" element={<Navigate to="/builder/powers" replace />} />

      {/* Redirect /admin to /admin/assemblies */}
      <Route path="/admin" element={<Navigate to="/admin/assemblies" replace />} />

      {/* Ruta por defecto */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <AssemblyProvider>
          <ConfirmDialogProvider>
            <AppWrapper>
              <Router>
                <NotificationSystem />
                <AppRoutes />
              </Router>
            </AppWrapper>
          </ConfirmDialogProvider>
        </AssemblyProvider>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
